import React, { useState, useEffect, useMemo } from 'react';
import { FormRenderer, fetchFormConfig } from '../hexaforms-shared';
import type { FormConfig } from '../hexaforms-shared';
import type { HexaFormProps } from './types';

type LoadStatus = 'loading' | 'ready' | 'error';

/**
 * Componente React para embeber formularios HexaForms nativamente.
 *
 * Requiere importar los estilos una vez en el entry point de tu app:
 *   import '@hexaforms/react/styles.css';
 *
 * @example
 * // En Next.js (app/layout.tsx o _app.tsx):
 * import '@hexaforms/react/styles.css';
 *
 * // En cualquier componente:
 * import { HexaForm } from '@hexaforms/react';
 *
 * <HexaForm
 *   formId="form_abc123"
 *   apiKey="fk_live_xyz789"
 *   theme={{ backgroundColor: '#0a0a0a', textColor: '#fff', primaryColor: '#8b5cf6' }}
 *   onSuccess={() => console.log('Submitted!')}
 * />
 */
export function HexaForm({
  formId,
  apiKey,
  theme: themeOverride,
  language,
  onSuccess,
  onError,
  className,
  style,
  loading = null,
  errorFallback,
}: HexaFormProps) {
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [baseConfig, setBaseConfig] = useState<FormConfig | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Carga la configuración del formulario desde el backend.
  // Solo re-fetcha si cambia formId o apiKey.
  useEffect(() => {
    let cancelled = false;

    setStatus('loading');
    setBaseConfig(null);
    setErrorMsg('');

    fetchFormConfig(formId, apiKey)
      .then(config => {
        if (cancelled) return;
        setBaseConfig(config);
        setStatus('ready');
      })
      .catch(err => {
        if (cancelled) return;
        const error = err instanceof Error ? err : new Error(String(err));
        setErrorMsg(error.message);
        setStatus('error');
        onError?.(error);
      });

    return () => {
      cancelled = true;
    };
  }, [formId, apiKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Combina el tema y el idioma del backend con los props.
  // Se recalcula en cada render si cambian, sin re-fetchar.
  const formConfig = useMemo<FormConfig | null>(() => {
    if (!baseConfig) return null;
    return {
      ...baseConfig,
      language: language ?? baseConfig.language,
      theme: themeOverride ? { ...baseConfig.theme, ...themeOverride } : baseConfig.theme,
    };
  }, [baseConfig, themeOverride, language]);

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className={className} style={style}>
        {loading}
      </div>
    );
  }

  // ─── Error ──────────────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div className={className} style={style}>
        {errorFallback ?? (
          <p style={{ color: '#ef4444', fontSize: '0.875rem', margin: 0 }}>
            Could not load form: {errorMsg}
          </p>
        )}
      </div>
    );
  }

  if (!formConfig) return null;

  // ─── Ready ──────────────────────────────────────────────────────────────────
  return (
    <div className={className} style={style}>
      <FormRenderer
        formConfig={formConfig}
        onSuccess={onSuccess}
        onError={onError}
      />
    </div>
  );
}
